//@flow
import React, {useState, useEffect} from 'react';
import {makeStyles, Typography, Grid, Hidden} from '@material-ui/core';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {Add, HelpTwoTone, PlayCircleFilledTwoTone} from '@material-ui/icons';
import {useHistory} from 'react-router-dom';

import {useDeviceReconnect, useStoreDevices} from '../utils';

import {colors} from '../../../../assets/styles/colors';

import PageSubtitle from '../../../../elements/PageSubTitle';
import ConfirmModal from '../../../../elements/Modals/ConfirmModal';

import ChatDeviceItem from './ChatDeviceItem';

import type {StoreDevice} from '../../../../entities';
import {TIENDADA_LINKS} from '../../../../constants';
import {useIsFeatureInTier} from '../../../../utils/component';
import {useTierRestrictionActions} from '../../../TierRestriction/utils';
import ConfirmModalDelete from './Modals/ConfirmModalDelete';
import UpgradeIcon from '../../../../elements/icons/UpgradeIcon';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '1px',
    gap: '.75rem',
    flexGrow: 1,
  },
  container: {
    background: 'white',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    padding: '1rem',
    height: '100%',
    minHeight: '68vh',
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    gap: '.5rem',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  headLine: {
    color: colors.grey700,
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
    [theme.breakpoints.down('xs')]: {
      fontSize: '18px',
    },
  },
  buttonLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  channelsList: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '1rem',
    padding: 0,
  },

  emptyChannels: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    flexDirection: 'column',
    gap: '1rem',
    maxWidth: '450px',
  },
  emptyChannelText: {
    color: colors.grey700,
    fontWeight: 600,
    fontSize: '1.5rem',
    lineHeight: '1.25',
  },
  addChannelButton: {
    borderRadius: '12px',
  },
  upgradeButton: {
    backgroundColor: 'white',
    color: colors.purple3,
    fontWeight: 600,
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    '&:hover': {
      backgroundColor: colors.grey200,
    },
  },
  upgradeButtonLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    justifyContent: 'center',
  },
}));

export default function Chat() {
  const classes = useStyles();
  const history = useHistory();

  const hasWhatsappWebFeature = useIsFeatureInTier('TIENDACHAT_WHATSAPP_WEB');
  const hasInstagramFeature = useIsFeatureInTier('TIENDACHAT_INSTAGRAM');
  const hasMessengerFeature = useIsFeatureInTier('TIENDACHAT_FB_MESSENGER');
  const hasWhatsappApiFeature = useIsFeatureInTier('TIENDACHAT_WHATSAPP_API');

  const canAddChannel =
    hasWhatsappWebFeature ||
    hasInstagramFeature ||
    hasMessengerFeature ||
    hasWhatsappApiFeature;

  const {showTierRestrictionModal} = useTierRestrictionActions();

  const handleButtonClick = e => {
    if (canAddChannel) return;
    e.preventDefault();
    showTierRestrictionModal();
  };

  const [deviceToDelete, setDeviceToDelete] = useState<StoreDevice | null>(
    null
  );
  const [
    deviceToReconnect,
    setDeviceToReconnect,
  ] = useState<StoreDevice | null>(null);

  const {qrCodeReconnect} = useDeviceReconnect();
  const {
    deleteDeviceWithHistory,
    deleteDeviceWithoutHistory,
    reconnectDevice,
    storeDevices,
    isLoading,
  } = useStoreDevices({
    refetchOnMount: true,
  });

  const devicesStoreStateConnected = storeDevices.filter(
    device => device.state === 'CONNECTED' || device.state === 'DISCONNECTED'
  );

  const handleRemoveDevice = (device: StoreDevice, withHistory: boolean) => {
    if (withHistory) {
      deleteDeviceWithHistory(device.id, device.service);
      setDeviceToDelete(null);
    } else {
      deleteDeviceWithoutHistory(device.id, device.service);
      setDeviceToDelete(null);
    }
  };

  useEffect(() => {
    if (qrCodeReconnect && deviceToReconnect) {
      history.push(`/chat/configuration/whatsapp`);
    }
  }, [deviceToReconnect, history, qrCodeReconnect]);

  return (
    <div className={classes.root}>
      <Grid
        container
        justify="space-between"
        alignItems="center"
        style={{width: '100%', padding: '0'}}
      >
        <Hidden smDown>
          <Grid item xs={6}>
            <Typography className={classes.title}>Chat</Typography>
          </Grid>
        </Hidden>
        <Grid item>
          <CustomButton
            href={TIENDADA_LINKS.customerSupport.general}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.ayudaButton}
            startIcon={<HelpTwoTone />}
          >
            Ayuda
          </CustomButton>
          <CustomButton
            href={TIENDADA_LINKS.tutorials.MULTICHAT}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.tutorialsButton}
            startIcon={<PlayCircleFilledTwoTone />}
          >
            Tutoriales
          </CustomButton>
        </Grid>
        <Grid item xs={12} style={{marginBlock: '.5rem'}}>
          <PageSubtitle name="Canales" />
        </Grid>
      </Grid>
      {!canAddChannel && (
        <CustomButton className={classes.upgradeButton} to="/tiers-list">
          <div className={classes.whiteButtonLabel}>
            <span>Mejora tu plan para acceder a esta funcionalidad</span>
            <UpgradeIcon width={18} height={18} />
          </div>
        </CustomButton>
      )}
      <div className={classes.container}>
        {devicesStoreStateConnected.length > 0 && !isLoading && (
          <header className={classes.header}>
            <Typography className={classes.headLine}>
              Integra diversos canales de comunicación y centraliza todo en
              TiendaDa.
            </Typography>
            <CustomButton
              onClick={handleButtonClick}
              variant="contained"
              disabled={!canAddChannel}
              to="/chat/configuration"
              className={classes.addChannelButton}
            >
              <span className={classes.buttonLabel}>
                <Add />
                <Typography>Agregar un nuevo canal</Typography>
              </span>
            </CustomButton>
          </header>
        )}

        {devicesStoreStateConnected.length === 0 && !isLoading ? (
          <div className={classes.emptyChannels}>
            <Typography
              variant="h6"
              align="center"
              className={classes.emptyChannelText}
            >
              Aún no tienes canales de comunicación
            </Typography>

            <Typography className={classes.headLine} align="center">
              Integra diversos canales de comunicación y centraliza todo en
              TiendaDa.
            </Typography>

            <CustomButton
              onClick={handleButtonClick}
              variant="contained"
              to="/chat/configuration"
              disabled={!canAddChannel}
              className={classes.addChannelButton}
            >
              <span className={classes.buttonLabel}>
                <Add />
                <Typography>Agregar canal</Typography>
              </span>
            </CustomButton>
          </div>
        ) : (
          <ul className={classes.channelsList}>
            {devicesStoreStateConnected.map(device => (
              <ChatDeviceItem
                key={`${device.id}-${device.service}`}
                device={device}
                onDelete={() => setDeviceToDelete(device)}
                onReconnect={() => {
                  setDeviceToReconnect(device);
                }}
              />
            ))}
          </ul>
        )}
      </div>
      <ConfirmModalDelete
        message="Si decides eliminar, todas tus conversaciones se borrarán y no lo podrás recuperar."
        title="¿Deseas eliminar este canal?"
        open={!!deviceToDelete}
        service={deviceToDelete?.service}
        onConfirmWithHistoryDelete={() => {
          if (!deviceToDelete) return;
          handleRemoveDevice(deviceToDelete, true);
        }}
        onConfirmWithoutHistoryDelete={() => {
          if (!deviceToDelete) return;
          handleRemoveDevice(deviceToDelete, false);
        }}
        onClose={() => setDeviceToDelete(null)}
      />
      <ConfirmModal
        message="¿Estás seguro que deseas reconnectar el dispositivo?"
        title="Reconectar dispositivo"
        open={!!deviceToReconnect}
        onConfirm={() => {
          if (!deviceToReconnect) return;
          reconnectDevice(deviceToReconnect.id, deviceToReconnect.service);
        }}
        onClose={() => setDeviceToReconnect(null)}
        onCancel={() => setDeviceToReconnect(null)}
      />
    </div>
  );
}
